import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { TRACKS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Copy, Pencil, Trash2, Library, Upload, Download, Paperclip, FileText } from "lucide-react";

const TYPE_LABELS = {
  bio: "Bios", abstract: "Abstracts", objectives: "Learning Objectives", pitch: "Pitch Variants",
  fee: "Fee Positioning", objection: "Objections / FAQs", logistics: "Logistics / Rider",
  attachment: "Attachments",
};
const TYPES = Object.keys(TYPE_LABELS);
const TEXT_TYPES = TYPES.filter((t) => t !== "attachment");

export default function AssetBank() {
  const qc = useQueryClient();
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: async () => (await api.get("/assets")).data });
  const [filter, setFilter] = useState("all");
  const refresh = () => qc.invalidateQueries({ queryKey: ["assets"] });

  const shown = filter === "all" ? assets : assets.filter((a) => a.type === filter);
  const grouped = TYPES.map((t) => ({ type: t, items: shown.filter((a) => a.type === t) })).filter((g) => g.items.length);

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="asset-bank-view">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Asset Bank</h1>
          <p className="text-sm text-muted-foreground mt-1">Managed, reusable content that all drafts are assembled from.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="asset-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}
            </SelectContent>
          </Select>
          <AssetDialog onDone={refresh} trigger={<Button variant="outline" className="gap-2" data-testid="add-asset-btn"><Plus className="h-4 w-4" /> New asset</Button>} />
          <UploadDialog onDone={refresh} />
        </div>
      </header>

      {grouped.map((g) => (
        <section key={g.type}>
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
            <Library className="h-4 w-4" /> {TYPE_LABELS[g.type]} <span className="opacity-60">· {g.items.length}</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {g.items.map((a) => (
              <Card key={a.id} className="p-4 border-border" data-testid={`asset-${a.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="font-medium text-sm">{a.title}</div>
                    <div className="flex items-center gap-1.5 mt-1">
                      {a.variant && <Badge variant="outline" className="text-[10px]">{a.variant}</Badge>}
                      <Badge variant="outline" className={`text-[10px] ${a.track === "all" ? "" : TRACKS[a.track]?.badge}`}>{a.track === "all" ? "all tracks" : TRACKS[a.track]?.short}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {a.is_attachment ? (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={async () => { try { await downloadFile(a.id, a.file_name); } catch { toast.error("Download failed"); } }} data-testid={`download-asset-${a.id}`}><Download className="h-3.5 w-3.5" /></Button>
                    ) : (
                      <>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { navigator.clipboard.writeText(a.content); toast.success("Copied"); }} data-testid={`copy-asset-${a.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                        <AssetDialog asset={a} onDone={refresh} trigger={<Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`edit-asset-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>} />
                      </>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={async () => { await api.delete(`/assets/${a.id}`); toast.success("Deleted"); refresh(); }} data-testid={`delete-asset-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                {a.is_attachment ? (
                  <button onClick={async () => { try { await downloadFile(a.id, a.file_name); } catch { toast.error("Download failed"); } }} className="flex items-center gap-3 w-full rounded-lg border border-border bg-muted/30 p-3 text-left hover:bg-accent transition-colors" data-testid={`attachment-${a.id}`}>
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 border border-primary/20">
                      <Paperclip className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{a.file_name}</div>
                      <div className="text-xs text-muted-foreground">{a.content_type} · {((a.file_size || 0) / 1024).toFixed(0)} KB</div>
                    </div>
                    <Download className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                ) : (
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{a.content}</p>
                )}
              </Card>
            ))}
          </div>
        </section>
      ))}
      {grouped.length === 0 && <p className="text-sm text-muted-foreground py-12 text-center">No assets yet.</p>}
    </div>
  );
}

function AssetDialog({ asset, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(asset || { type: "bio", title: "", variant: "", track: "all", content: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    try {
      const payload = { ...form, variant: form.variant || null };
      if (asset) await api.patch(`/assets/${asset.id}`, payload);
      else await api.post("/assets", payload);
      toast.success(asset ? "Asset updated" : "Asset created");
      setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o && !asset) setForm({ type: "bio", title: "", variant: "", track: "all", content: "" }); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{asset ? "Edit asset" : "New asset"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="asset-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TEXT_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Track</Label>
            <Select value={form.track} onValueChange={(v) => setForm({ ...form, track: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All tracks</SelectItem>{Object.entries(TRACKS).map(([id, t]) => <SelectItem key={id} value={id}>{t.short}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={set("title")} data-testid="asset-title-input" /></div>
          <div className="space-y-1.5"><Label>Variant (optional)</Label><Input value={form.variant || ""} onChange={set("variant")} placeholder="short / medium / long" /></div>
          <div className="col-span-2 space-y-1.5"><Label>Content</Label><Textarea value={form.content} onChange={set("content")} rows={6} data-testid="asset-content-input" /></div>
        </div>
        <DialogFooter><Button disabled={!form.title || !form.content} onClick={submit} data-testid="asset-save-btn">{asset ? "Save changes" : "Create asset"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [track, setTrack] = useState("all");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      fd.append("type", "attachment");
      fd.append("track", track);
      await api.post("/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded");
      setOpen(false); setTitle(""); setFile(null); setTrack("all"); onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setTitle(""); setFile(null); } }}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="upload-asset-btn"><Upload className="h-4 w-4" /> Upload file</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Paperclip className="h-4 w-4 text-primary" /> Upload attachment</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Add a file (PDF, deck, image, doc) to the asset bank. Max 25 MB.</p>
        <div className="space-y-4 pt-1">
          <div className="space-y-1.5"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Percussion Pals Deck" data-testid="upload-title-input" /></div>
          <div className="space-y-1.5"><Label>Track</Label>
            <Select value={track} onValueChange={setTrack}>
              <SelectTrigger data-testid="upload-track-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All tracks</SelectItem>{Object.entries(TRACKS).map(([id, t]) => <SelectItem key={id} value={id}>{t.short}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>File</Label>
            <label className="flex items-center gap-3 rounded-lg border border-dashed border-border p-4 cursor-pointer hover:bg-accent/40 transition-colors" data-testid="upload-dropzone">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground truncate">{file ? file.name : "Choose a file…"}</span>
              <input type="file" className="hidden" data-testid="upload-file-input" onChange={(e) => { const f = e.target.files?.[0]; setFile(f); if (f && !title) setTitle(f.name); }} />
            </label>
          </div>
        </div>
        <DialogFooter><Button disabled={!file || busy} onClick={submit} data-testid="upload-submit-btn">{busy ? "Uploading…" : "Upload"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
